import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { SectionTitle, StatusPill, EmptyState, Avatar } from "../../components/Atoms";
import { RichHtml } from "../../components/Atoms";
import BulkTaskUpload from "../../components/BulkTaskUpload";
import {
  ArrowLeft, Stack, Calendar, ListChecks, Users, Megaphone, CheckCircle, XCircle,
  Link as LinkIcon, FileArrowDown, CaretDown, CaretRight, ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function TaskCard({ task, allSubs, onAct }) {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState({});
  const subs = allSubs.filter(s => s.task_id === task.id);
  const pending = subs.filter(s => s.status === "Pending").length;
  const approved = subs.filter(s => s.status === "Approved").length;
  const rejected = subs.filter(s => s.status === "Rejected").length;

  return (
    <div className="glass p-4" data-testid={`admin-task-card-${task.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 text-left" data-testid={`task-toggle-${task.id}`}>
        <div className="mt-1 text-muted">{open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-white font-bold truncate">{task.title}</div>
            {((task.platforms && task.platforms.length > 0) ? task.platforms : (task.platform ? [task.platform] : [])).map(pl => (
              <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
            ))}
            <StatusPill status={task.status || "Active"} />
          </div>
          <div className="text-[11px] text-muted mt-1 flex flex-wrap gap-3">
            <span>Reward: <span className="text-white font-semibold">{fmtMoney(task.reward_amount)}</span></span>
            {task.bonus_amount ? <span>Bonus: <span className="text-[#A3E635] font-semibold">{fmtMoney(task.bonus_amount)}</span></span> : null}
            {task.deadline && <span>Due: <span className="text-white">{fmtDate(task.deadline)}</span></span>}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 text-xs flex-shrink-0">
          {pending > 0 && <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/30 font-bold">{pending} pending</span>}
          {approved > 0 && <span className="px-2 py-0.5 rounded-full bg-[#A3E635]/10 text-[#A3E635] border border-[#A3E635]/30 font-bold">{approved} ok</span>}
          {rejected > 0 && <span className="px-2 py-0.5 rounded-full bg-[#EC4899]/10 text-[#EC4899] border border-[#EC4899]/30 font-bold">{rejected} rej</span>}
          {subs.length === 0 && <span className="text-muted">No submissions</span>}
        </div>
      </button>

      {open && (
        <div className="mt-4 pl-7 space-y-3 border-l border-white/10">
          {(task.instructions || task.proof_required) && (
            <div className="text-xs text-muted bg-white/5 rounded-xl p-3 space-y-1.5">
              {task.instructions && <div><strong className="text-white">Instructions:</strong> <RichHtml html={task.instructions} /></div>}
              {task.proof_required && <div><strong className="text-white">Proof required:</strong> {task.proof_required}</div>}
            </div>
          )}

          <div className="text-xs uppercase font-bold text-muted tracking-wider mt-2">
            Submissions ({subs.length})
          </div>

          {subs.length === 0 ? (
            <div className="text-sm text-muted italic">No creator submissions yet.</div>
          ) : (
            <div className="space-y-3">
              {subs.map(s => (
                <div key={s.id} className="p-3 rounded-2xl bg-white/3 border border-white/5" data-testid={`task-sub-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <Link to={`/admin/ambassadors/${s.ambassador_id}`} className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80" data-testid={`sub-amb-link-${s.id}`}>
                      <Avatar src={s.ambassador_photo} name={s.ambassador_name} />
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold truncate">{s.ambassador_name}</div>
                        <div className="text-[11px] text-muted truncate">Submitted {fmtDate(s.created_at)}</div>
                      </div>
                    </Link>
                    <StatusPill status={s.status} />
                  </div>
                  {s.notes && <div className="text-sm text-muted mt-2 ml-1">{s.notes}</div>}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {s.link && <a href={s.link} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><LinkIcon size={14} /> Open link</a>}
                    {s.file_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1.5"><FileArrowDown size={14} /> File</a>}
                  </div>
                  {s.feedback && <div className="text-xs text-[#F59E0B] mt-2">Feedback: {s.feedback}</div>}
                  {s.status === "Pending" && (
                    <div className="mt-3 space-y-2">
                      <input placeholder="Feedback (optional)" value={feedback[s.id] || ""} onChange={e => setFeedback({...feedback, [s.id]: e.target.value})} className="input-base text-sm" data-testid={`task-sub-feedback-${s.id}`} />
                      <div className="flex gap-2">
                        <button onClick={() => onAct(s.id, "approve", feedback[s.id] || "")} className="btn-ghost flex-1 text-[#A3E635] flex items-center justify-center gap-1.5 text-sm" data-testid={`task-sub-approve-${s.id}`}><CheckCircle size={16} weight="bold" /> Approve</button>
                        <button onClick={() => onAct(s.id, "reject", feedback[s.id] || "")} className="btn-ghost flex-1 text-[#EC4899] flex items-center justify-center gap-1.5 text-sm" data-testid={`task-sub-reject-${s.id}`}><XCircle size={16} weight="bold" /> Reject</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminProjectDetailPage() {
  const { pid } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/projects/${pid}`),
        api.get(`/submissions?project_id=${pid}`),
      ]);
      setProject(p.data);
      setSubs(s.data);
    } finally { setLoading(false); }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const act = async (sid, action, fb) => {
    try {
      await api.post(`/submissions/${sid}/${action}`, { feedback: fb });
      toast.success(`Submission ${action}d`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <div className="skeleton h-64" />;
  if (!project) return <EmptyState title="Project not found" />;

  const tasks = project.tasks || [];
  const totalSub = subs.length;
  const pending = subs.filter(s => s.status === "Pending").length;
  const approved = subs.filter(s => s.status === "Approved").length;
  const totalReward = tasks.reduce((a, t) => a + (t.reward_amount || 0) + (t.bonus_amount || 0), 0);

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-from-project">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Project overview */}
      <div className="glass p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-muted">{project.client_name || "—"} · {project.category || "—"}</div>
            <div className="text-white text-2xl font-bold mt-1">{project.title}</div>
            {project.description && <RichHtml html={project.description} className="text-sm text-muted mt-2 max-w-2xl" />}
            <div className="text-[11px] text-muted mt-3 flex items-center gap-1"><Calendar size={12} /> {fmtDate(project.start_date)} → {fmtDate(project.end_date)}</div>
          </div>
          <StatusPill status={project.status} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-5">
          <div className="bg-white/5 rounded-2xl p-3"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><ListChecks size={12} /> Tasks</div><div className="text-white text-lg font-bold mt-1">{tasks.length}</div></div>
          <div className="bg-white/5 rounded-2xl p-3"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><Users size={12} /> Creators</div><div className="text-white text-lg font-bold mt-1">{project.ambassador_count || 0}</div></div>
          <div className="bg-white/5 rounded-2xl p-3"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><Megaphone size={12} /> Submissions</div><div className="text-white text-lg font-bold mt-1">{totalSub}</div></div>
          <div className="bg-white/5 rounded-2xl p-3"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><CheckCircle size={12} /> Approved</div><div className="text-[#A3E635] text-lg font-bold mt-1">{approved}</div></div>
          <div className="bg-white/5 rounded-2xl p-3"><div className="text-[10px] uppercase text-muted font-bold flex items-center gap-1"><ChartLineUp size={12} /> Total reward</div><div className="text-white text-lg font-bold mt-1">{fmtMoney(totalReward)}</div></div>
        </div>
        {pending > 0 && (
          <div className="mt-3 text-xs text-[#F59E0B] font-bold">
            {pending} submission{pending > 1 ? "s" : ""} awaiting review.
          </div>
        )}
      </div>

      {/* Task cards */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <SectionTitle title={`Tasks (${tasks.length})`} subtitle="Each task and its creator submissions" />
        <div className="flex items-center gap-2">
          <BulkTaskUpload projectId={pid} onSuccess={load} dataTestid="admin-bulk-tasks-btn" />
          <Link to={`/admin/projects?manage=${pid}`} className="btn-ghost text-xs flex items-center gap-1.5 text-[#22D3EE]" data-testid="manage-tasks-link">
            + Add / edit tasks
          </Link>
        </div>
      </div>
      {tasks.length === 0 ? <EmptyState icon={Stack} title="No tasks defined yet" subtitle="Add tasks from the Projects page." /> : (
        <div className="space-y-3" data-testid="admin-tasks-list">
          {tasks.map(t => <TaskCard key={t.id} task={t} allSubs={subs} onAct={act} />)}
        </div>
      )}
    </div>
  );
}
